import React from 'react';
import { Radio } from 'antd';

// Off/Alert/Block segmented control — the author-facing collapse of the Trex
// `enable/remediate/inline` triplet (the David-SE-console "mode" UX, re-expressed on
// corenet/antd primitives). This is a reusable policy-form primitive: other BlueRock
// surfaces that edit a rule triplet should use it rather than re-deriving the mapping.
//
// Enable-only stanzas (no `remediate`) pass supportsBlock=false and get Off/Alert only.

/**
 * The control's public vocabulary — the three author-facing modes. Owned here so the
 * reusable component does not depend on any consumer (e.g. the descriptor codegen); the
 * mode<->triplet mapping lives with the consumer that knows a stanza's capabilities.
 */
export type PolicyMode = 'off' | 'alert' | 'block';

export interface PolicyModeOption {
  value: PolicyMode;
  label: string;
  help: string;
}

const ALL_OPTIONS: PolicyModeOption[] = [
  { value: 'off', label: 'Off', help: 'Rule disabled — no detection.' },
  { value: 'alert', label: 'Alert', help: 'Enabled — detect and log only, no enforcement.' },
  { value: 'block', label: 'Block', help: 'Enabled and enforced — block or kill the action.' },
];

export interface PolicyModeControlProps {
  value: PolicyMode;
  onChange: (mode: PolicyMode) => void;
  /** Whether the stanza carries `remediate` — when false, Block is hidden. */
  supportsBlock?: boolean;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  'aria-label'?: string;
}

export const PolicyModeControl: React.FC<PolicyModeControlProps> = ({
  value,
  onChange,
  supportsBlock = true,
  disabled,
  size = 'middle',
  'aria-label': ariaLabel,
}) => {
  const options = supportsBlock ? ALL_OPTIONS : ALL_OPTIONS.filter((o) => o.value !== 'block');
  return (
    <Radio.Group
      value={value}
      onChange={(e) => onChange(e.target.value as PolicyMode)}
      optionType="button"
      buttonStyle="solid"
      size={size}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {options.map((o) => (
        <Radio.Button key={o.value} value={o.value} title={o.help}>
          {o.label}
        </Radio.Button>
      ))}
    </Radio.Group>
  );
};

export default PolicyModeControl;
